/**
 * @voltxampere/engine — Transient analysis orchestrator
 *
 * Sprint 6's headline class. The solver wires the DC operating
 * point handoff, the SPECS §3.9 adaptive-step controller, the
 * SPECS §3.8 BE/TRAP/Gear-2 companions, and the per-step Newton-
 * Raphson into a single async entry point that takes a {@link Circuit}
 * and returns a {@link TransientResult}.
 *
 * Algorithm (per SPECS §3.9):
 *
 *   1. Validate registries (DC + transient stamps).
 *   2. Plan the circuit.
 *   3. Solve DC at t=0 → initial operating point.
 *   4. Apply explicit initial conditions on capacitors / inductors
 *      where the user pinned `initialVoltage` / `initialCurrent`.
 *   5. Step from t=tstart to t=tstop:
 *      a. Predict x(t+h) by linear extrapolation.
 *      b. Newton-correct using the transient MNA.
 *      c. Compute LTE = ||x_corr − x_pred||_∞.
 *      d. Adaptive controller: accept (advance t, maybe grow h) or
 *         reject (halve h, retry).
 *      e. On accept, recompute per-component currents (TRAP cap).
 *
 * Sprint 6 scope: DC sources only (no pulse / sine / PWL — those
 * land in Sprint 7+ as bespoke transient stamps). The solver runs
 * synchronously inside `Promise<TransientResult>` so Sprint 9's
 * Worker offload is a one-line implementation swap rather than an
 * API break.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { ConvergenceError, TransientStepFailedError } from "@voltxampere/shared";
import { DCSolver } from "../dc/solver.js";
import { planCircuit } from "../dc/circuit-plan.js";
import { areDCStampsRegistered, areTransientStampsRegistered } from "../stamps/register.js";
import { getNumericParam, getTerminalNetId } from "../stamps/terminal-utils.js";
import { decideAdaptiveStep, defaultAdaptiveStepConstants } from "./adaptive-step.js";
import { linearPredictor } from "./predictor.js";
import { newtonRaphsonTransient } from "./newton-raphson.js";
import { transientSourceStepping } from "./source-stepper.js";
import { transientGminStepping } from "./gmin-stepper.js";
import { runAssertionsOnTransient } from "../assertion/run.js";
import { resolveIntegrationMethod } from "./integration/types.js";
const DEFAULT_MAX_NEWTON_ITERATIONS = 50;
export class TransientSolver {
    async solve(circuit, options) {
        if (!areDCStampsRegistered()) {
            throw new ConvergenceError("TransientSolver.solve called before registerDCStamps() — DC operating point requires the DC dispatch table");
        }
        if (!areTransientStampsRegistered()) {
            throw new ConvergenceError("TransientSolver.solve called before registerTransientStamps() — no transient dispatch table");
        }
        const settings = circuit.simulationSettings.transient;
        const method = resolveIntegrationMethod(settings.method);
        const tstart = settings.tstart;
        const tstop = settings.tstop;
        const reltol = settings.reltol;
        const abstol = settings.abstol;
        const maxNewton = options?.maxNewtonIterations ?? DEFAULT_MAX_NEWTON_ITERATIONS;
        const plan = planCircuit(circuit);
        // Sprint 6.1 P1-1: honour the user's `tstepMax` setting if
        // provided. It caps the adaptive controller's upper bound so a
        // user asking for, say, 1ns fidelity on a 1ms run gets it
        // regardless of the default `tstop/10 = 1e-4` ceiling.
        const constants = settings.tstepMax !== undefined
            ? defaultAdaptiveStepConstants({ tstop, userTstepMax: settings.tstepMax })
            : defaultAdaptiveStepConstants({ tstop });
        // 1. DC operating point — gives us the t=tstart starting state.
        const dcSolver = new DCSolver();
        const dcResult = await dcSolver.solve(circuit);
        const x0 = stateFromDCResult(plan, dcResult);
        // Sprint 6.1 P1-3: seed the Newton counter with the DC OP's
        // iterations (including any gmin/source-stepping rescue iters)
        // so the reported `totalNewtonIterations` reflects the *entire*
        // computational effort, not just per-transient-step work.
        const dcIterations = dcResult.iterations;
        const dcFallback = dcResult.usedFallback;
        const dcClampEvents = dcResult.clampEvents;
        // 2. Apply explicit ICs on top of the DC OP.
        applyInitialConditions(circuit, plan, x0);
        // 3. Initialise per-component current history for TRAP cap.
        const componentCurrents = new Map();
        initializeCapacitorCurrents(circuit, plan, x0, componentCurrents);
        // 4. Initialise loop state.
        let h = clampInitialStep(options?.initialStep ?? tstop / 1000, constants.hMin, constants.hMax);
        let hPrev = h;
        let t = tstart;
        let xPrev = x0;
        let xPrevPrev = null;
        let firstStep = true;
        const timeSteps = [snapshotAt(plan, t, xPrev)];
        let stepsAccepted = 0;
        let stepsRejected = 0;
        // Seeded with the DC OP's iteration count (Sprint 6.1 P1-3).
        let totalNewtonIterations = dcIterations;
        // Sprint 8a.1: seed with the DC OP's clamp events (they matter
        // for diode / BJT circuits whose DC operating point already
        // exercised the trust-region helper).
        let totalClampEvents = dcClampEvents;
        const previousCurrentLookup = (id) => componentCurrents.get(id) ?? 0;
        let nextProgressPercent = 10;
        // 5. Time-stepping loop (SPECS §3.9 predictor-corrector).
        while (t < tstop) {
            options?.signal?.throwIfAborted();
            if (options?.onProgress) {
                const pct = Math.min(99, ((t - tstart) / (tstop - tstart)) * 100);
                if (pct >= nextProgressPercent) {
                    options.onProgress(pct, `t=${t.toExponential(3)}`);
                    nextProgressPercent = Math.ceil(pct / 10) * 10 + 10;
                }
            }
            // Don't overshoot the user's tstop boundary.
            const remaining = tstop - t;
            if (h > remaining)
                h = remaining;
            const xPred = linearPredictor(xPrev, xPrevPrev, h, hPrev);
            const baseNewtonOpts = {
                maxIterations: maxNewton,
                reltol,
                abstol,
                x0: xPred,
                xStepPrev: xPrev,
                xStepPrevPrev: xPrevPrev,
                t: t + h,
                h,
                hPrev,
                method,
                firstStep,
                previousCurrent: previousCurrentLookup,
            };
            let newtonResult;
            let newtonNonFiniteCause = null;
            try {
                newtonResult = newtonRaphsonTransient(circuit, plan, baseNewtonOpts);
            }
            catch (e) {
                if (!(e instanceof ConvergenceError))
                    throw e;
                newtonNonFiniteCause = e;
                // Sprint 54.3g engine ext #8 + Sprint 54.3f ext #7 — per-step
                // rescue ladder. Order: gmin-stepping (addresses Y matrix
                // ill-conditioning, the dominant cause of "Newton fails even
                // at h_min" symptoms) → source-stepping (addresses
                // source-amplitude stiffness, the dominant cause of "diodes
                // suddenly conduct at large h" symptoms) → fall through to
                // h-halving. The gmin → source order is chosen because gmin
                // is a strict superset: when source-stepping converges, gmin
                // would also have converged trivially at any level (the
                // regularisation is unnecessary but harmless); when
                // ill-conditioning is the issue, gmin converges where
                // source-stepping cannot.
                let rescued;
                try {
                    const gminRescue = transientGminStepping(circuit, plan, baseNewtonOpts, {
                        maxIterations: maxNewton,
                    });
                    rescued = gminRescue.result;
                    totalNewtonIterations += gminRescue.totalIterations - gminRescue.result.iterations;
                    totalClampEvents += gminRescue.totalClampEvents - gminRescue.result.clampEvents;
                }
                catch {
                    try {
                        const sourceRescue = transientSourceStepping(circuit, plan, baseNewtonOpts, {
                            maxIterations: maxNewton,
                        });
                        rescued = sourceRescue.result;
                        totalNewtonIterations += sourceRescue.totalIterations - sourceRescue.result.iterations;
                        totalClampEvents += sourceRescue.totalClampEvents - sourceRescue.result.clampEvents;
                    }
                    catch {
                        // Both rescues failed. Fall back to h-halving.
                        const next = h * constants.rejectFactor;
                        if (next < constants.hMin) {
                            throw new TransientStepFailedError(`transient: Newton did not converge and h reached h_min (gmin + source-stepping rescues also failed)`, {
                                meta: {
                                    t,
                                    h,
                                    hMin: constants.hMin,
                                    attempts: stepsRejected,
                                    cause: "newton-non-finite",
                                },
                                cause: newtonNonFiniteCause instanceof Error ? newtonNonFiniteCause : undefined,
                            });
                        }
                        stepsRejected++;
                        h = Math.max(next, constants.hMin);
                        continue;
                    }
                }
                newtonResult = rescued;
            }
            if (!newtonResult.converged) {
                // Sprint 54.3g engine ext #8 + 54.3f ext #7 — same rescue
                // ladder for the !converged branch (Newton hit maxIter rather
                // than throwing on non-finite). Order: gmin → source →
                // h-halving.
                let rescued;
                try {
                    const gminRescue = transientGminStepping(circuit, plan, baseNewtonOpts, {
                        maxIterations: maxNewton,
                    });
                    rescued = gminRescue.result;
                    totalNewtonIterations += gminRescue.totalIterations - gminRescue.result.iterations;
                    totalClampEvents += gminRescue.totalClampEvents - gminRescue.result.clampEvents;
                }
                catch {
                    try {
                        const sourceRescue = transientSourceStepping(circuit, plan, baseNewtonOpts, {
                            maxIterations: maxNewton,
                        });
                        rescued = sourceRescue.result;
                        totalNewtonIterations += sourceRescue.totalIterations - sourceRescue.result.iterations;
                        totalClampEvents += sourceRescue.totalClampEvents - sourceRescue.result.clampEvents;
                    }
                    catch {
                        const next = h * constants.rejectFactor;
                        if (next < constants.hMin) {
                            throw new TransientStepFailedError(`transient: Newton hit maxIterations and h reached h_min (gmin + source-stepping rescues also failed)`, {
                                meta: {
                                    t,
                                    h,
                                    hMin: constants.hMin,
                                    attempts: stepsRejected,
                                    cause: "newton-max-iter",
                                },
                            });
                        }
                        stepsRejected++;
                        h = Math.max(next, constants.hMin);
                        continue;
                    }
                }
                newtonResult = rescued;
            }
            const xCorr = newtonResult.x;
            totalNewtonIterations += newtonResult.iterations;
            totalClampEvents += newtonResult.clampEvents;
            // Sprint 54.3h engine ext #10 — LTE check excludes
            // discrete-state-toggling rows (volatile nodes). The predictor
            // is linear; nodes like LM393 comp_out swing V_OL ↔ HIGH
            // between adjacent time-steps, which a linear extrapolation
            // cannot track. Without exclusion, the LTE blows up to several
            // volts every time a comparator toggles, forcing the
            // adaptive-step controller to halve h until h_min — the exact
            // failure signature of the fathers transient (54.3g-4
            // re-diagnosis).
            const lte = vectorNormInfDiffExcluding(xCorr, xPred, plan.volatileIndices);
            const xNorm = vectorNormInf(xCorr);
            // The first transient step has no meaningful predictor (xPrev
            // doubled-as-cold-start) and the IC override may have left
            // xPrev inconsistent with KCL/KVL — V_ind / I_L can jump to a
            // self-consistent value that bears no relation to xPrev. The
            // LTE there reflects the IC discontinuity, not the integration
            // error. Force-accept the first step (Newton's converged x
            // *is* the right answer for that step) and let the controller
            // start working from step 2 onward when xPrev is a real
            // post-step solution.
            const decision = firstStep
                ? { action: "accept", nextH: h, threshold: 0 }
                : decideAdaptiveStep({
                    currentH: h,
                    lte,
                    xNormInf: xNorm,
                    reltol,
                    abstol,
                    constants,
                });
            if (decision.action === "reject") {
                // Sprint 6.1 P0-1: the previous predicate was
                //   `nextH <= hMin && nextH < h`
                // which failed to fire when h was already at hMin (the
                // controller's `Math.max(shrunk, hMin)` clamp produces
                // nextH == hMin == h, `nextH < h` is false, no throw,
                // infinite loop with t never advancing). `<=` alone is the
                // correct predicate: if the next step we're asked to try
                // is at or below hMin, we've run out of headroom — the
                // controller already clamped nextH to the floor, and
                // re-entering with the same (h, x_prev) would produce the
                // same reject verdict on the next iteration.
                if (decision.nextH <= constants.hMin) {
                    throw new TransientStepFailedError(`transient: adaptive-step controller exhausted h reductions`, {
                        meta: {
                            t,
                            h,
                            hMin: constants.hMin,
                            attempts: stepsRejected,
                            lastLte: lte,
                            threshold: decision.threshold,
                        },
                    });
                }
                stepsRejected++;
                h = decision.nextH;
                continue;
            }
            // Accept step.
            stepsAccepted++;
            const tNext = t + h;
            const xNext = xCorr;
            // Update per-component currents (TRAP capacitor needs i_C(t)).
            updateCapacitorCurrents(circuit, plan, xPrev, xNext, h, method, componentCurrents);
            xPrevPrev = xPrev;
            xPrev = xNext;
            hPrev = h;
            h = decision.nextH;
            t = tNext;
            firstStep = false;
            timeSteps.push(snapshotAt(plan, t, xNext));
        }
        // Sprint 9: evaluate assertions against the transient run.
        // Same error-handling discipline as DCSolver: parse/eval
        // errors become failed AssertionResult entries rather than
        // propagating out of the solver.
        const pre = {
            converged: true,
            timeSteps,
            stepsAccepted,
            stepsRejected,
            totalNewtonIterations,
            dcOperatingPointFallback: dcFallback,
            method,
            totalClampEvents,
            assertionResults: [],
        };
        const assertionResults = runAssertionsOnTransient(circuit, pre);
        return { ...pre, assertionResults };
    }
}
// ─────────────────────────────────────────────────────────────────────
// Internals
// ─────────────────────────────────────────────────────────────────────
function clampInitialStep(h, hMin, hMax) {
    if (!Number.isFinite(h) || h <= 0) {
        throw new ConvergenceError(`TransientSolver: initial step must be a positive finite number, got ${String(h)}`);
    }
    return Math.max(Math.min(h, hMax), hMin);
}
/** Reconstruct an x vector (length plan.size) from the DCResult maps. */
function stateFromDCResult(plan, dc) {
    const x = new Float64Array(plan.size);
    for (const [netId, idx] of plan.netToIndex.entries()) {
        x[idx] = dc.nodeVoltages.get(netId) ?? 0;
    }
    for (const [compId, idx] of plan.vSourceIdToIndex.entries()) {
        // Sprint 24 B1 — vsource rows live AFTER the internal-node block.
        x[plan.nodeCount + plan.internalNodeCount + idx] = dc.sourceCurrents.get(compId) ?? 0;
    }
    return x;
}
/**
 * Override the DC OP with explicit IC values pinned on capacitors
 * (`initialVoltage`) and inductors (`initialCurrent`). Sprint 6
 * supports the common case where the capacitor's negative terminal
 * is grounded; floating-cap ICs need a "use IC" DC re-solve which
 * is deferred to Sprint 7+.
 */
function applyInitialConditions(circuit, plan, x) {
    for (const c of circuit.components) {
        if (c.type === "capacitor") {
            const ic = c.params.initialVoltage;
            if (ic === undefined)
                continue;
            const value = typeof ic === "number" ? ic : null;
            if (value === null)
                continue;
            const posNet = getTerminalNetId(c, "p1");
            const negNet = getTerminalNetId(c, "p2");
            const posIdx = plan.netToIndex.get(posNet);
            const negIdx = plan.netToIndex.get(negNet);
            const posIsGround = posNet === plan.groundNetId;
            const negIsGround = negNet === plan.groundNetId;
            if (negIsGround && posIdx !== undefined) {
                x[posIdx] = value;
            }
            else if (posIsGround && negIdx !== undefined) {
                x[negIdx] = -value;
            }
            else {
                // Floating cap IC — Sprint 7+. For now, leave DC OP intact.
                // (The IC is silently ignored; sprint report notes this.)
            }
        }
        else if (c.type === "inductor") {
            const ic = c.params.initialCurrent;
            if (ic === undefined)
                continue;
            const value = typeof ic === "number" ? ic : null;
            if (value === null)
                continue;
            const k = plan.vSourceIdToIndex.get(c.id);
            if (k !== undefined) {
                // Sprint 24 B1 — vsource rows live AFTER the internal-node block.
                x[plan.nodeCount + plan.internalNodeCount + k] = value;
            }
        }
    }
}
/**
 * Compute per-capacitor i_C(0) from the initial state for use as
 * the first step's `previousCurrent`. The DC OP says i_C = 0 at
 * steady state, but if an IC overrode V_C the computed i_C will
 * still be 0 at t=0 because dV/dt = 0 by definition of "initial".
 * The map stays seeded for accumulation in subsequent accepted
 * steps.
 */
function initializeCapacitorCurrents(circuit, plan, _x0, out) {
    for (const c of circuit.components) {
        if (c.type === "capacitor") {
            out.set(c.id, 0);
        }
    }
    // Inductor "currents" come from the augmented row, not this map —
    // we leave them out.
    void plan;
}
/**
 * After an accepted step at time t, recompute per-capacitor i_C(t)
 * using the integration-method's own formula. Only the TRAP
 * capacitor stamp consumes this in the next step's Ieq; BE / Gear-2
 * cap stamps don't need i_C history. We update unconditionally so
 * mid-run method switches (Sprint 7+ idea) can pick up the right
 * value.
 *
 * BE:     i_C(t) = (C/h) · (V_C(t) − V_C(t−h))
 * TRAP:   i_C(t) = (2C/h) · (V_C(t) − V_C(t−h)) − i_C(t−h)
 * Gear-2: i_C(t) = (3C/(2h)) · V_C(t) − (2C/h) · V_C(t−h) +
 *                  (C/(2h)) · V_C(t−2h)
 *         (deferred — Gear-2 cap doesn't read i_C from history,
 *          so we use the BE-equivalent here for safety)
 */
function updateCapacitorCurrents(circuit, plan, xStepPrev, xCurr, h, method, cur) {
    for (const c of circuit.components) {
        if (c.type !== "capacitor")
            continue;
        const C = getNumericParam(c, "capacitance");
        const posNet = getTerminalNetId(c, "p1");
        const negNet = getTerminalNetId(c, "p2");
        const VCcurr = nodeVoltageAt(plan, xCurr, posNet) - nodeVoltageAt(plan, xCurr, negNet);
        const VCprev = nodeVoltageAt(plan, xStepPrev, posNet) - nodeVoltageAt(plan, xStepPrev, negNet);
        let iC;
        switch (method) {
            case "BE":
                iC = (C / h) * (VCcurr - VCprev);
                break;
            case "TRAP": {
                const iCPrev = cur.get(c.id) ?? 0;
                iC = ((2 * C) / h) * (VCcurr - VCprev) - iCPrev;
                break;
            }
            case "Gear-2":
                // Gear-2 cap doesn't consume i_C history; use the BE-style
                // forward difference as a sane default for the map.
                iC = (C / h) * (VCcurr - VCprev);
                break;
        }
        cur.set(c.id, iC);
    }
}
function nodeVoltageAt(plan, x, netId) {
    if (netId === plan.groundNetId)
        return 0;
    const idx = plan.netToIndex.get(netId);
    if (idx === undefined)
        return 0;
    return x[idx] ?? 0;
}
function snapshotAt(plan, t, x) {
    const nodeVoltages = new Map();
    nodeVoltages.set(plan.groundNetId, 0);
    for (const [netId, idx] of plan.netToIndex.entries()) {
        nodeVoltages.set(netId, x[idx] ?? 0);
    }
    const sourceCurrents = new Map();
    for (const [compId, idx] of plan.vSourceIdToIndex.entries()) {
        // Sprint 24 B1 — vsource rows live AFTER the internal-node block.
        sourceCurrents.set(compId, x[plan.nodeCount + plan.internalNodeCount + idx] ?? 0);
    }
    return { t, nodeVoltages, sourceCurrents };
}
function vectorNormInf(x) {
    let m = 0;
    for (const value of x) {
        const v = Math.abs(value);
        if (v > m)
            m = v;
    }
    return m;
}
/**
 * Sprint 54.3h engine ext #10 — LTE infinity norm with volatile-row
 * exclusion. Same shape as the original Sprint 6 `vectorNormInfDiff`
 * but skips MNA row indices listed in `volatileIndices`. Used by the
 * adaptive-step controller to ignore predictor-vs-corrector gaps on
 * discrete-state-toggling nodes (LM393 comp_out, TRIAC state row,
 * MOC state row, b-source ternary output). When `volatileIndices`
 * is empty (linear circuits) the function reduces to the standard
 * infinity-norm — cost regression preserved.
 */
function vectorNormInfDiffExcluding(a, b, volatileIndices) {
    if (a.length !== b.length) {
        throw new ConvergenceError(`vectorNormInfDiffExcluding: length mismatch ${String(a.length)} vs ${String(b.length)}`);
    }
    let m = 0;
    for (let i = 0; i < a.length; i++) {
        if (volatileIndices.has(i))
            continue;
        const v = Math.abs((a[i] ?? 0) - (b[i] ?? 0));
        if (v > m)
            m = v;
    }
    return m;
}
//# sourceMappingURL=solver.js.map